import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtUSD } from "@/lib/format";
import {
  Smartphone, Globe, CreditCard, Shield, DollarSign, Wallet,
  Server, Route, RefreshCw, Send, Search, Users, Building2,
  Database, Layers, Network, FileCheck, Lock, Activity,
  CheckCircle2, ArrowDown, ArrowUp, Play, RotateCcw,
  Fingerprint, Gauge, Calculator, Split, BookOpen,
  Bell, KeyRound, ScanLine, Hash, Clock, TrendingUp,
} from "lucide-react";

const KACYBER_RATE = 0.02;
const PLATFORM_SPREAD = 0.008;

type Tone = "blue" | "cyan" | "forest" | "orange" | "purple" | "amber" | "rose";

const TONES: Record<Tone, { bg: string; border: string; text: string; dot: string; ring: string; glow: string }> = {
  blue:   { bg: "bg-blue-500/10",    border: "border-blue-400/30",    text: "text-blue-400",    dot: "bg-blue-500",    ring: "ring-blue-400/40",    glow: "shadow-blue-500/20" },
  cyan:   { bg: "bg-cyan-500/10",    border: "border-cyan-400/30",    text: "text-cyan-400",    dot: "bg-cyan-500",    ring: "ring-cyan-400/40",    glow: "shadow-cyan-500/20" },
  forest: { bg: "bg-emerald-500/10", border: "border-emerald-400/30", text: "text-emerald-400", dot: "bg-emerald-500", ring: "ring-emerald-400/40", glow: "shadow-emerald-500/20" },
  orange: { bg: "bg-orange-500/10",  border: "border-orange-400/30",  text: "text-orange-400",  dot: "bg-orange-500",  ring: "ring-orange-400/40",  glow: "shadow-orange-500/20" },
  purple: { bg: "bg-purple-500/10",  border: "border-purple-400/30",  text: "text-purple-400",  dot: "bg-purple-500",  ring: "ring-purple-400/40",  glow: "shadow-purple-500/20" },
  amber:  { bg: "bg-amber-500/10",   border: "border-amber-400/30",   text: "text-amber-400",   dot: "bg-amber-500",   ring: "ring-amber-400/40",   glow: "shadow-amber-500/20" },
  rose:   { bg: "bg-rose-500/10",    border: "border-rose-400/30",    text: "text-rose-400",    dot: "bg-rose-500",    ring: "ring-rose-400/40",    glow: "shadow-rose-500/20" },
};

type IconType = typeof Smartphone;

type SubNode = { id: string; label: string; icon: IconType; desc: string };
type LayerDef = {
  id: string;
  name: string;
  desc: string;
  icon: IconType;
  tone: Tone;
  subNodes: SubNode[];
  highlight?: boolean;
};

const LAYERS: LayerDef[] = [
  {
    id: "customer", name: "Customer Layer", desc: "Multi-channel entry · Parent / Student", icon: Smartphone, tone: "blue",
    subNodes: [
      { id: "customer-mobile", label: "Mobile App", icon: Smartphone, desc: "Parent / student" },
      { id: "customer-web", label: "Web Portal", icon: Globe, desc: "Browser access" },
      { id: "customer-pos", label: "POS / API", icon: CreditCard, desc: "Direct integration" },
    ],
  },
  {
    id: "gateway", name: "BRECCSPay Gateway", desc: "API gateway · security · validation", icon: Server, tone: "cyan",
    subNodes: [
      { id: "gateway-auth", label: "Authentication", icon: KeyRound, desc: "JWT / API key" },
      { id: "gateway-ratelimit", label: "Rate Limiting", icon: Gauge, desc: "Throttle & quota" },
      { id: "gateway-validate", label: "Validation", icon: ScanLine, desc: "Schema check" },
    ],
  },
  {
    id: "engine", name: "Payment Engine", desc: "Core processing · fraud · pricing · settlement", icon: DollarSign, tone: "forest",
    subNodes: [
      { id: "engine-fraud", label: "Fraud Engine", icon: Fingerprint, desc: "Velocity · device · IP" },
      { id: "engine-pricing", label: "Pricing Engine", icon: Calculator, desc: "Fee calculation" },
      { id: "engine-settlement", label: "Settlement Engine", icon: Split, desc: "Batch rules" },
    ],
  },
  {
    id: "orchestration", name: "Orchestration Layer", desc: "Smart routing · retries · callbacks", icon: Route, tone: "purple",
    subNodes: [
      { id: "orchestration-routing", label: "Smart Routing", icon: Route, desc: "Provider selection" },
      { id: "orchestration-retry", label: "Retry Engine", icon: RefreshCw, desc: "Exponential backoff" },
      { id: "orchestration-callback", label: "Callback Handler", icon: Send, desc: "Webhook processor" },
    ],
  },
  {
    id: "kacyber", name: "KaCyberPay", desc: "Primary payment rail · MoMo collection", icon: Network, tone: "orange", highlight: true,
    subNodes: [
      { id: "kacyber-lookup", label: "Account Lookup", icon: Search, desc: "Phone verification" },
      { id: "kacyber-push", label: "MoMo Collection", icon: Send, desc: "Push to handset" },
      { id: "kacyber-callback", label: "Webhook Callback", icon: RefreshCw, desc: "HMAC verified" },
    ],
  },
  {
    id: "wallet", name: "Wallet & Ledger", desc: "Double-entry books · per-child wallets", icon: Wallet, tone: "forest",
    subNodes: [
      { id: "wallet-student", label: "Student Wallet", icon: Users, desc: "Credit balance" },
      { id: "wallet-merchant", label: "Merchant Wallet", icon: Building2, desc: "School balance" },
      { id: "wallet-ledger", label: "Ledger Entry", icon: BookOpen, desc: "Debit / credit" },
    ],
  },
  {
    id: "settlement", name: "Settlement & Reconciliation", desc: "Batch payout · audit · receipt", icon: FileCheck, tone: "amber",
    subNodes: [
      { id: "settlement-batch", label: "Batch Settlement", icon: Layers, desc: "Daily / weekly" },
      { id: "settlement-reconcile", label: "Auto-Reconciliation", icon: CheckCircle2, desc: "Ledger match" },
      { id: "settlement-receipt", label: "Receipt & Notify", icon: Bell, desc: "SMS / email" },
    ],
  },
];

type PhaseDef = {
  id: number;
  label: string;
  activeNodes: string[];
  log: string;
  detail: string;
  isReturn?: boolean;
  duration: number;
};

const PHASES: PhaseDef[] = [
  {
    id: 0, label: "Press Run to process a payment", activeNodes: [], log: "", detail: "",
    duration: 0,
  },
  {
    id: 1, label: "Customer initiates payment",
    activeNodes: ["customer", "customer-mobile"],
    log: "Customer opens BRECCSPay app — selects 'Pay School Fees'",
    detail: "Parent selects multiple children and multiple services (fees, transport, canteen, uniform). The app bundles them into a single consolidated invoice with one PRN.",
    duration: 1300,
  },
  {
    id: 2, label: "Gateway authenticates request",
    activeNodes: ["gateway", "gateway-auth"],
    log: "Gateway: JWT validated — API key verified — session active",
    detail: "The gateway checks the JWT bearer token, validates the API key, and confirms the session is active. No request proceeds without authentication.",
    duration: 1300,
  },
  {
    id: 3, label: "Rate limiting & validation",
    activeNodes: ["gateway", "gateway-ratelimit", "gateway-validate"],
    log: "Gateway: rate limit OK (12 req/min) — schema validated — amount > 0",
    detail: "Rate limiter checks the parent hasn't exceeded 12 requests/minute. Schema validator confirms amount is positive, PRN format is valid, and all required fields are present.",
    duration: 1300,
  },
  {
    id: 4, label: "Fraud engine scans for risk",
    activeNodes: ["engine", "engine-fraud"],
    log: "Fraud: velocity OK — device fingerprint matched — IP reputation clean",
    detail: "The fraud engine checks transaction velocity (not too many payments in a short window), matches the device fingerprint against known devices, and verifies IP reputation against threat intelligence feeds.",
    duration: 1500,
  },
  {
    id: 5, label: "Pricing engine calculates fees",
    activeNodes: ["engine", "engine-pricing"],
    log: "Pricing: 2.8% platform fee — KaCyberPay 2.0% rail — BRECCSPay 0.8% spread",
    detail: "The pricing engine calculates the total 2.8% platform fee. Of this, 2.0% goes to KaCyberPay as the payment rail, and 0.8% is retained by BRECCSPay as platform revenue. The merchant (school) receives the full principal.",
    duration: 1500,
  },
  {
    id: 6, label: "Settlement engine queues batch",
    activeNodes: ["engine", "engine-settlement"],
    log: "Settlement: batch rule matched — T+1 payout — merchant wallet credited",
    detail: "The settlement engine determines the batch rule (T+1 daily payout to the school's merchant wallet) and queues the transaction for the next settlement window.",
    duration: 1300,
  },
  {
    id: 7, label: "Smart routing selects KaCyberPay",
    activeNodes: ["orchestration", "orchestration-routing"],
    log: "Routing: KaCyberPay selected — lowest cost — 99.2% success rate — <8s avg",
    detail: "The smart router evaluates all available connectors. KaCyberPay wins on cost (2% vs 3% for alternatives) and success rate (99.2%). The router falls back to other providers if KaCyberPay is unavailable.",
    duration: 1500,
  },
  {
    id: 8, label: "Retry engine on standby",
    activeNodes: ["orchestration", "orchestration-retry"],
    log: "Retry: policy loaded — 3 attempts — exponential backoff (2s, 4s, 8s)",
    detail: "The retry engine loads its policy: 3 attempts with exponential backoff (2s, 4s, 8s). It will only activate if KaCyberPay returns a transient failure.",
    duration: 1300,
  },
  {
    id: 9, label: "KaCyberPay account lookup",
    activeNodes: ["kacyber", "kacyber-lookup"],
    log: "KaCyberPay: account lookup — 0772 123 456 — MTN — registered — KYC verified",
    detail: "KaCyberPay queries the MTN MoMo API to verify the phone number is registered and KYC-verified. This prevents payments to unregistered or fraudulent numbers.",
    duration: 1500,
  },
  {
    id: 10, label: "KaCyberPay sends MoMo push",
    activeNodes: ["kacyber", "kacyber-push"],
    log: "KaCyberPay: MoMo collection push sent — awaiting customer PIN on handset",
    detail: "KaCyberPay sends a collection request to MTN MoMo. The customer receives a USSD prompt on their handset asking them to enter their MoMo PIN to authorize the payment.",
    duration: 1500,
  },
  {
    id: 11, label: "Customer confirms with PIN",
    activeNodes: ["customer"],
    log: "Customer entered PIN — payment authorized on handset — MTN confirms",
    detail: "The customer enters their MoMo PIN on their handset. MTN validates the PIN and debits the customer's MoMo wallet. The payment is now authorized.",
    duration: 1500,
  },
  {
    id: 12, label: "KaCyberPay webhook callback",
    activeNodes: ["kacyber", "kacyber-callback"],
    log: "KaCyberPay: webhook received — HMAC signature verified — status: confirmed",
    detail: "KaCyberPay receives a webhook from MTN confirming the payment. The HMAC signature is verified to ensure the callback is authentic and hasn't been tampered with.",
    duration: 1500,
    isReturn: true,
  },
  {
    id: 13, label: "Callback handler processes",
    activeNodes: ["orchestration", "orchestration-callback"],
    log: "Callback handler: idempotency check passed — transaction status updated",
    detail: "The callback handler checks idempotency (ensuring this callback hasn't been processed before), updates the transaction status to 'confirmed', and triggers the wallet credit.",
    duration: 1500,
    isReturn: true,
  },
  {
    id: 14, label: "Student wallet credited",
    activeNodes: ["wallet", "wallet-student"],
    log: "Wallets: student wallet credited — balance updated — WTX generated",
    detail: "The student's wallet is credited with the payment amount. A unique WTX (wallet transaction number) is generated for each child's service line, enabling per-line traceability.",
    duration: 1500,
  },
  {
    id: 15, label: "Merchant wallet & ledger posted",
    activeNodes: ["wallet", "wallet-merchant", "wallet-ledger"],
    log: "Wallets: merchant wallet credited — ledger double-entry posted — audit trail created",
    term: "ledger",
    detail: "The school's merchant wallet is credited. The ledger creates a double-entry: debit the clearing account, credit the merchant wallet. This maintains balanced books and a full audit trail.",
    duration: 1500,
  } as any,
  {
    id: 16, label: "Batch settlement queued",
    activeNodes: ["settlement", "settlement-batch"],
    log: "Settlement: queued for next batch — T+1 payout — auto-reconciled",
    detail: "The transaction is queued for the next settlement batch (T+1 daily payout). The settlement engine will transfer funds from the clearing account to the school's bank account.",
    duration: 1500,
  },
  {
    id: 17, label: "Auto-reconciliation & receipt",
    activeNodes: ["settlement", "settlement-reconcile", "settlement-receipt"],
    log: "Reconciliation: ledger matched — receipt issued — SMS sent to parent",
    detail: "The auto-reconciliation engine matches the ledger entry against the KaCyberPay callback. A receipt is generated and sent to the parent via SMS. The transaction is now fully settled.",
    duration: 1500,
  },
  {
    id: 18, label: "Transaction complete",
    activeNodes: [],
    log: "Done — receipt sent — both wallets updated — ledger balanced",
    detail: "The payment is complete. KaCyberPay earned 2% as the payment rail. BRECCSPay earned 0.8% as the platform spread. The school received the full principal. Both wallets grew.",
    duration: 1500,
  },
];

const ARROW_PHASES = [2, 4, 7, 9, 14, 16];

const LAYER_LAST_PHASE: Record<string, number> = {};
PHASES.forEach(p => {
  LAYERS.forEach(layer => {
    const isActive = p.activeNodes.includes(layer.id) || layer.subNodes.some(sn => p.activeNodes.includes(sn.id));
    if (isActive) LAYER_LAST_PHASE[layer.id] = Math.max(LAYER_LAST_PHASE[layer.id] ?? 0, p.id);
  });
});

const AMOUNTS = [
  { v: 10000, l: "10K" },
  { v: 50000, l: "50K" },
  { v: 100000, l: "100K" },
  { v: 500000, l: "500K" },
];

export function PlatformArchitecture() {
  const [phase, setPhase] = useState(0);
  const [amount, setAmount] = useState(100000);
  const [kacyberWallet, setKacyberWallet] = useState(0);
  const [tricampusWallet, setTricampusWallet] = useState(0);
  const [txnCount, setTxnCount] = useState(0);
  const [logs, setLogs] = useState<Array<{ id: number; text: string; time: string; phaseId: number }>>([]);
  const timers = useRef<number[]>([]);
  const logId = useRef(0);

  const kacyberCut = Math.round(amount * KACYBER_RATE);
  const tricampusCut = Math.round(amount * PLATFORM_SPREAD);
  const totalFee = kacyberCut + tricampusCut;

  const reset = useCallback(() => {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current = [];
    setPhase(0);
    setKacyberWallet(0);
    setTricampusWallet(0);
    setTxnCount(0);
    setLogs([]);
    logId.current = 0;
  }, []);

  const play = useCallback(() => {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current = [];
    setPhase(0);
    setLogs([]);
    logId.current = 0;

    let elapsed = 0;
    PHASES.forEach((p) => {
      if (p.id === 0) return;
      elapsed += p.duration;
      timers.current.push(window.setTimeout(() => {
        setPhase(p.id);
        if (p.log) {
          const now = new Date();
          const time = now.toLocaleTimeString("en-GB", { hour12: false }) + "." + String(now.getMilliseconds()).padStart(3, "0");
          logId.current += 1;
          setLogs(prev => [{ id: logId.current, text: p.log, time, phaseId: p.id }, ...prev].slice(0, 40));
        }
        if (p.id === 15) {
          setKacyberWallet(w => w + kacyberCut);
          setTricampusWallet(w => w + tricampusCut);
          setTxnCount(c => c + 1);
        }
      }, elapsed));
    });
  }, [kacyberCut, tricampusCut]);

  useEffect(() => () => { timers.current.forEach(t => window.clearTimeout(t)); }, []);

  const currentPhase = PHASES[phase];
  const isActive = phase > 0 && phase < 18;
  const isDone = phase === 18;

  return (
    <Card className="overflow-hidden border-0 bg-gradient-to-br from-[#0a1a14] via-[#0f2a20] to-[#0a261d] p-0 text-primary-foreground shadow-2xl">
      {/* ===== HEADER ===== */}
      <div className="border-b border-white/10 p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Badge className="bg-white/15 text-primary-foreground hover:bg-white/20">
                <Layers className="mr-1 h-3 w-3" /> Platform Architecture
              </Badge>
              <Badge className="bg-[#A42022]/30 text-primary-foreground hover:bg-[#A42022]/40">
                <Network className="mr-1 h-3 w-3" /> KaCyberPay Rail
              </Badge>
            </div>
            <h3 className="mt-3 font-display text-2xl sm:text-3xl">The Payment Operating System</h3>
            <p className="mt-1 max-w-xl text-sm text-primary-foreground/70">
              KaCyberPay is the first payment connector — invisible to customers, swappable for future providers.
              Like AWS behind Netflix. Press Run to watch a payment flow through every layer in detail.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset}
                className="border-white/20 text-primary-foreground hover:bg-white/10 hover:text-primary-foreground">
                <RotateCcw className="mr-1 h-3 w-3" />Reset
              </Button>
              <Button size="sm" onClick={play} className="bg-[var(--mustard)] text-foreground hover:opacity-90">
                <Play className="mr-1 h-3 w-3" />Run flow
              </Button>
            </div>
            <div className="flex items-center gap-1.5">
              {AMOUNTS.map(p => (
                <button key={p.v} onClick={() => setAmount(p.v)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                    amount === p.v ? "bg-white/20 text-primary-foreground" : "bg-white/5 text-primary-foreground/50 hover:bg-white/10"
                  }`}>
                  USD {p.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Phase progress bar */}
        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full transition ${isActive ? "bg-[var(--mustard)] animate-pulse" : isDone ? "bg-emerald-400" : "bg-white/30"}`} />
              <span className="text-sm text-primary-foreground/80">{currentPhase.label}</span>
            </div>
            <span className="font-mono text-[11px] text-primary-foreground/50">{phase}/{PHASES.length - 1}</span>
          </div>
          <div className="flex gap-0.5">
            {PHASES.slice(1).map((p) => (
              <div key={p.id} className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                <motion.div
                  className={`h-full rounded-full ${p.isReturn ? "bg-orange-400" : "bg-[var(--mustard)]"}`}
                  initial={{ width: 0 }}
                  animate={{ width: phase > p.id ? "100%" : phase === p.id ? "50%" : "0%" }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== MAIN: flow diagram + event log ===== */}
      <div className="grid gap-0 lg:grid-cols-[1fr_340px]">
        {/* Flow diagram */}
        <div className="border-b border-white/10 p-6 sm:p-8 lg:border-b-0 lg:border-r">
          <div className="space-y-0">
            {LAYERS.map((layer, idx) => {
              const layerActive = currentPhase.activeNodes.includes(layer.id);
              const anySubActive = layer.subNodes.some(sn => currentPhase.activeNodes.includes(sn.id));
              const lastPhase = LAYER_LAST_PHASE[layer.id] ?? 0;
              const isDone = phase > lastPhase && lastPhase > 0;

              return (
                <div key={layer.id}>
                  <LayerCard
                    layer={layer}
                    active={layerActive || anySubActive}
                    done={isDone}
                    activeNodes={currentPhase.activeNodes}
                  />
                  {idx < LAYERS.length - 1 && (
                    <ConnectionArrow
                      active={phase >= ARROW_PHASES[idx] && phase < 18}
                      returnActive={phase === 12 || phase === 13}
                      showReturn={idx === 3}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Future connectors */}
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-3">
            <span className="text-[11px] uppercase tracking-wider text-primary-foreground/50">Future connectors</span>
            {["MTN Direct", "Airtel Direct", "Flutterwave", "Pesapal", "Bank APIs"].map(p => (
              <span key={p} className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] text-primary-foreground/40">{p}</span>
            ))}
          </div>
        </div>

        {/* Event log + detail panel */}
        <div className="flex flex-col bg-black/20">
          {/* Detail panel */}
          <div className="border-b border-white/10 p-4 sm:p-6">
            <div className="mb-2 flex items-center gap-2">
              <Hash className="h-3.5 w-3.5 text-primary-foreground/50" />
              <span className="text-xs font-medium uppercase tracking-wider text-primary-foreground/50">Step Detail</span>
            </div>
            <AnimatePresence mode="wait">
              {currentPhase.detail ? (
                <motion.div
                  key={phase}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--mustard)]/20 font-mono text-[10px] text-[var(--mustard)]">
                      {phase}
                    </span>
                    <span className="text-sm font-medium">{currentPhase.label}</span>
                  </div>
                  <p className="text-[12px] leading-relaxed text-primary-foreground/70">
                    {currentPhase.detail}
                  </p>
                </motion.div>
              ) : (
                <div className="text-xs text-primary-foreground/30">Press Run flow to see step-by-step detail.</div>
              )}
            </AnimatePresence>
          </div>

          {/* Event log */}
          <div className="flex-1 p-4 sm:p-6">
            <div className="mb-3 flex items-center gap-2">
              <Activity className="h-3.5 w-3.5 text-primary-foreground/50" />
              <span className="text-xs font-medium uppercase tracking-wider text-primary-foreground/50">Live Event Log</span>
            </div>
            <div className="h-[400px] space-y-1.5 overflow-y-auto pr-1">
              <AnimatePresence initial={false}>
                {logs.map(l => (
                  <motion.div
                    key={l.id}
                    initial={{ opacity: 0, x: -10, height: 0 }}
                    animate={{ opacity: 1, x: 0, height: "auto" }}
                    className="rounded-lg border border-white/10 bg-white/5 p-2 text-[11px] leading-relaxed"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[9px] text-primary-foreground/40">{l.time}</span>
                      <span className="font-mono text-[9px] text-primary-foreground/30">#{l.phaseId}</span>
                    </div>
                    <p className="mt-0.5 text-primary-foreground/80">{l.text}</p>
                  </motion.div>
                ))}
              </AnimatePresence>
              {logs.length === 0 && (
                <div className="grid h-full place-items-center text-center text-primary-foreground/30">
                  <div>
                    <Activity className="mx-auto mb-2 h-6 w-6" />
                    <p className="text-xs">Press Run flow to see live events</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Revenue counters ===== */}
      <div className="grid gap-0 border-t border-white/10 sm:grid-cols-2">
        <RevenueCounter
          name="KaCyberPay Revenue"
          subtitle="2.0% payment rail fee"
          balance={kacyberWallet}
          txnCount={txnCount}
          tone="orange"
          icon={Network}
          highlighted={phase === 15}
        />
        <RevenueCounter
          name="BRECCSPay Revenue"
          subtitle="0.8% platform spread"
          balance={tricampusWallet}
          txnCount={txnCount}
          tone="forest"
          icon={Wallet}
          highlighted={phase === 15}
        />
      </div>

      {/* ===== Per-tx breakdown ===== */}
      <div className="grid grid-cols-2 gap-px border-t border-white/10 bg-white/5 sm:grid-cols-4">
        <BreakdownTile label="Customer pays" value={fmtUSD(amount + totalFee)} sub="Principal + 2.8% fee" tone="blue" />
        <BreakdownTile label="KaCyberPay earns" value={fmtUSD(kacyberCut)} sub="2.0% rail" tone="orange" />
        <BreakdownTile label="BRECCSPay earns" value={fmtUSD(tricampusCut)} sub="0.8% spread" tone="forest" />
        <BreakdownTile label="Merchant receives" value={fmtUSD(amount)} sub="Full principal" tone="purple" />
      </div>

      {/* ===== Unit economics ===== */}
      <div className="border-t border-white/10 p-6 sm:p-8">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[var(--mustard)]" />
          <span className="text-sm font-medium uppercase tracking-wider text-primary-foreground/60">Unit Economics · Why This Is Profitable</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <UnitEcon
            label="Cost per transaction"
            value={fmtUSD(kacyberCut)}
            sub="KaCyberPay 2% rail fee"
            tone="rose"
            desc="The only direct cost. No infrastructure spend — KaCyberPay handles MoMo collection, account lookup, and callbacks."
          />
          <UnitEcon
            label="Revenue per transaction"
            value={fmtUSD(totalFee)}
            sub="2.8% total platform fee"
            tone="forest"
            desc="Charged to the customer on top of the principal. The school always receives the full amount."
          />
          <UnitEcon
            label="Gross profit per transaction"
            value={fmtUSD(tricampusCut)}
            sub="0.8% net margin"
            tone="amber"
            desc="Revenue minus KaCyberPay cost. This is BRECCSPay's take-home on every payment processed."
          />
          <UnitEcon
            label="Gross margin"
            value="28.6%"
            sub="0.8% ÷ 2.8% = 28.6%"
            tone="cyan"
            desc="For every USD 100 collected in fees, BRECCSPay keeps USD 28.60. Scales linearly with volume — no fixed costs."
          />
        </div>

        {/* Scale projection */}
        <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="mb-3 text-xs uppercase tracking-wider text-primary-foreground/50">Monthly projection · at current amount</div>
          <div className="grid gap-4 sm:grid-cols-3">
            <ScaleRow label="100 txns / month" kacyber={kacyberCut * 100} tricampus={tricampusCut * 100} />
            <ScaleRow label="1,000 txns / month" kacyber={kacyberCut * 1000} tricampus={tricampusCut * 1000} />
            <ScaleRow label="10,000 txns / month" kacyber={kacyberCut * 10000} tricampus={tricampusCut * 10000} />
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ============================================================
   Sub-components
============================================================ */

function LayerCard({ layer, active, done, activeNodes }: {
  layer: LayerDef;
  active: boolean;
  done: boolean;
  activeNodes: string[];
}) {
  const ts = TONES[layer.tone];
  const LayerIcon = layer.icon;

  return (
    <motion.div
      animate={{
        scale: active ? 1.02 : 1,
        opacity: done && !active ? 0.5 : 1,
      }}
      transition={{ duration: 0.4 }}
      className={`relative overflow-hidden rounded-xl border p-4 transition-all duration-300 ${
        active
          ? `${ts.border} ${ts.bg} ring-2 ${ts.ring} shadow-lg ${ts.glow}`
          : done
            ? "border-white/10 bg-white/[0.02]"
            : "border-white/10 bg-white/[0.03]"
      } ${layer.highlight ? "border-orange-400/30" : ""}`}
    >
      {layer.highlight && (
        <div className="absolute right-3 top-3">
          <Badge className="bg-[var(--mustard)]/30 text-primary-foreground hover:bg-[var(--mustard)]/40 text-[9px]">PRIMARY</Badge>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className={`grid h-10 w-10 place-items-center rounded-lg transition ${active ? ts.bg : "bg-white/5"}`}>
          <LayerIcon className={`h-5 w-5 transition ${active ? ts.text : "text-primary-foreground/40"}`} />
        </div>
        <div>
          <div className={`font-medium text-sm transition ${active ? "text-primary-foreground" : "text-primary-foreground/60"}`}>
            {layer.name}
          </div>
          <div className="text-[11px] text-primary-foreground/40">{layer.desc}</div>
        </div>
        {done && !active && <CheckCircle2 className="ml-auto h-4 w-4 text-emerald-400/60" />}
        {active && (
          <motion.div
            className="ml-auto h-2 w-2 rounded-full bg-[var(--mustard)]"
            animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 0.8, repeat: Infinity }}
          />
        )}
      </div>

      {/* Sub-nodes */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {layer.subNodes.map(sn => {
          const snActive = activeNodes.includes(sn.id);
          const SnIcon = sn.icon;
          return (
            <motion.div
              key={sn.id}
              animate={{ scale: snActive ? 1.05 : 1, opacity: snActive ? 1 : 0.4 }}
              transition={{ duration: 0.3 }}
              className={`flex flex-col items-center rounded-lg border p-2 text-center transition ${
                snActive ? `${ts.border} ${ts.bg}` : "border-white/5 bg-white/[0.02]"
              }`}
            >
              <SnIcon className={`h-4 w-4 transition ${snActive ? ts.text : "text-primary-foreground/30"}`} />
              <div className={`mt-1 text-[10px] font-medium transition ${snActive ? "text-primary-foreground" : "text-primary-foreground/40"}`}>
                {sn.label}
              </div>
              <div className="text-[9px] text-primary-foreground/30">{sn.desc}</div>
              {snActive && (
                <motion.div
                  className={`mt-1 h-0.5 w-full rounded-full ${ts.dot}`}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.4 }}
                />
              )}
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

function ConnectionArrow({ active, returnActive, showReturn }: {
  active: boolean;
  returnActive?: boolean;
  showReturn?: boolean;
}) {
  return (
    <div className="flex justify-center py-1.5">
      <div className="flex items-center gap-3">
        {/* Down arrow (forward flow) */}
        <motion.div
          animate={{ opacity: active ? 1 : 0.15 }}
          transition={{ duration: 0.4 }}
          className="relative"
        >
          <ArrowDown className={`h-4 w-4 transition ${active ? "text-[var(--mustard)]" : "text-white/20"}`} />
          {active && (
            <motion.div
              className="absolute left-1/2 top-0 h-1 w-1 -translate-x-1/2 rounded-full bg-[var(--mustard)]"
              animate={{ y: [0, 20], opacity: [1, 0] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          )}
        </motion.div>

        {/* Up arrow (callback return) */}
        {showReturn && (
          <motion.div
            animate={{ opacity: returnActive ? 1 : 0.15 }}
            transition={{ duration: 0.4 }}
            className="relative flex items-center gap-1"
          >
            <span className={`text-[9px] font-medium transition ${returnActive ? "text-orange-400" : "text-white/20"}`}>Callback</span>
            <ArrowUp className={`h-4 w-4 transition ${returnActive ? "text-orange-400" : "text-white/20"}`} />
            {returnActive && (
              <motion.div
                className="absolute right-0 top-0 h-1 w-1 rounded-full bg-orange-400"
                animate={{ y: [20, 0], opacity: [1, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

function RevenueCounter({ name, subtitle, balance, txnCount, tone, icon: Icon, highlighted }: {
  name: string;
  subtitle: string;
  balance: number;
  txnCount: number;
  tone: Tone;
  icon: IconType;
  highlighted: boolean;
}) {
  const ts = TONES[tone];
  return (
    <motion.div
      animate={{ scale: highlighted ? 1.02 : 1 }}
      transition={{ duration: 0.4 }}
      className={`p-5 transition ${highlighted ? ts.bg : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`grid h-8 w-8 place-items-center rounded-lg ${ts.bg}`}>
            <Icon className={`h-4 w-4 ${ts.text}`} />
          </div>
          <div>
            <div className="text-sm font-medium">{name}</div>
            <div className="text-[10px] text-primary-foreground/40">{subtitle}</div>
          </div>
        </div>
        <Badge variant="outline" className="border-white/20 text-[10px] text-primary-80">
          {txnCount} txn{txnCount !== 1 ? "s" : ""}
        </Badge>
      </div>
      <motion.div
        key={balance}
        initial={{ scale: 1.15, color: tone === "forest" ? "#34d399" : "#fb923c" }}
        animate={{ scale: 1, color: "inherit" }}
        transition={{ duration: 0.5 }}
        className="mt-3 font-display text-2xl"
      >
        {fmtUSD(balance)}
      </motion.div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
        <motion.div
          className={`h-full rounded-full ${ts.dot}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, (balance / 1000000) * 100)}%` }}
          transition={{ duration: 0.6 }}
        />
      </div>
    </motion.div>
  );
}

function BreakdownTile({ label, value, sub, tone }: {
  label: string;
  value: string;
  sub?: string;
  tone: Tone;
}) {
  const ts = TONES[tone];
  return (
    <div className="bg-[#0a1a14] p-3">
      <div className="text-[10px] uppercase tracking-wider text-primary-foreground/40">{label}</div>
      <div className={`mt-1 font-display text-lg ${ts.text}`}>{value}</div>
      {sub && <div className="text-[10px] text-primary-foreground/30">{sub}</div>}
    </div>
  );
}

function UnitEcon({ label, value, sub, tone, desc }: {
  label: string;
  value: string;
  sub: string;
  tone: Tone;
  desc: string;
}) {
  const ts = TONES[tone];
  return (
    <div className={`rounded-xl border p-4 ${ts.border} ${ts.bg}`}>
      <div className="text-[10px] uppercase tracking-wider text-primary-foreground/50">{label}</div>
      <div className={`mt-1 font-display text-2xl ${ts.text}`}>{value}</div>
      <div className="text-[11px] text-primary-foreground/40">{sub}</div>
      <p className="mt-2 text-[11px] leading-relaxed text-primary-foreground/50">{desc}</p>
    </div>
  );
}

function ScaleRow({ label, kacyber, tricampus }: { label: string; kacyber: number; tricampus: number }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <div className="text-[11px] text-primary-foreground/50">{label}</div>
      <div className="mt-1.5 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-orange-400/70">KaCyberPay</span>
          <span className="font-mono text-xs text-orange-400">{fmtUSD(kacyber)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-emerald-400/70">BRECCSPay</span>
          <span className="font-mono text-xs text-emerald-400">{fmtUSD(tricampus)}</span>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 pt-1">
          <span className="text-[10px] text-primary-foreground/50">Combined</span>
          <span className="font-mono text-xs text-primary-foreground">{fmtUSD(kacyber + tricampus)}</span>
        </div>
      </div>
    </div>
  );
}
